/** Dev/Preview timing marks for Canvas generation submit lifecycle
 * (VISTOERA_CANVAS_GENERATE_IMMEDIATE_FEEDBACK_AND_SUBMISSION_LATENCY_FORENSIC_P0_V1). */

export type CanvasGenerationTimingMark =
  | 'T0_CLICK'
  | 'T1_VISIBLE_FEEDBACK'
  | 'T2_FIELDS_PATCHED'
  | 'T5_REQUEST_START'
  | 'T11_JOB_ACCEPTED'

const marks = new Map<string, Partial<Record<CanvasGenerationTimingMark, number>>>()

function telemetryEnabled(): boolean {
  if (typeof import.meta !== 'undefined' && import.meta.env.DEV) return true
  if (typeof import.meta !== 'undefined' && import.meta.env.VITE_CANVAS_GENERATION_TIMING === '1') return true
  if (typeof window !== 'undefined' && window.location.hostname.includes('preview')) return true
  return false
}

export function markCanvasGenerationTiming(nodeId: string, mark: CanvasGenerationTimingMark): void {
  if (!telemetryEnabled()) return
  const key = nodeId
  const row = marks.get(key) ?? {}
  if (row[mark] == null) {
    row[mark] = performance.now()
    marks.set(key, row)
  }
}

export function readCanvasGenerationTiming(nodeId: string): Partial<Record<CanvasGenerationTimingMark, number>> {
  return marks.get(nodeId) ?? {}
}

export function summarizeCanvasGenerationTiming(nodeId: string): Record<string, number | null> {
  const row = readCanvasGenerationTiming(nodeId)
  const t0 = row.T0_CLICK ?? null
  const delta = (mark: CanvasGenerationTimingMark): number | null => {
    const t = row[mark]
    if (t0 == null || t == null) return null
    return Math.round(t - t0)
  }
  return {
    CLICK_TO_VISIBLE_FEEDBACK_MS: delta('T1_VISIBLE_FEEDBACK'),
    CLICK_TO_FIELDS_PATCHED_MS: delta('T2_FIELDS_PATCHED'),
    CLICK_TO_REQUEST_START_MS: delta('T5_REQUEST_START'),
    CLICK_TO_JOB_ACCEPTED_MS: delta('T11_JOB_ACCEPTED'),
  }
}

export function logCanvasGenerationTiming(nodeId: string): void {
  if (!telemetryEnabled()) return
  const summary = summarizeCanvasGenerationTiming(nodeId)
  console.info('[CanvasGenerationTiming]', JSON.stringify({ nodeId, ...summary }))
  if (typeof window !== 'undefined') {
    const w = window as unknown as { __CANVAS_GENERATION_TIMING__?: Record<string, unknown> }
    w.__CANVAS_GENERATION_TIMING__ = { nodeId, ...summary }
  }
}
